import { useEffect, useState } from "react";

import { GET_SCHEDULED_REVENUE_DAILY } from "../../api/kpi";
import { periodEndFor } from "../../helpers/workingDays";
import { companyToday } from "../../helpers/companyTime";
import { lookAheadEnd } from "./lookAhead";
import { resolveWindow } from "../../pages/CompanyMetrics/dashboards/useWorkspaceData";

/**
 * How much of each day's Total Scheduled Revenue belongs to jobs that have
 * since been completed. Only asked for when the card is showing Total Scheduled
 * Revenue, and only ever used to shade the bars — the bar heights and the figure
 * above them still come from the same scores every other card reads, so this
 * can add a shade to a bar but never change its height.
 *
 * Returns `series: null` until the answer is in (or when it is not asked for or
 * cannot be had), which the chart reads as "draw plain bars" — a failed request
 * must leave the card as it was, not blank it.
 *
 * `status` is "idle" (not asked for), "loading", "ready" or "error". The card
 * uses it to put the legend in place BEFORE the answer arrives: the legend takes
 * a row of height, and adding it after the bars had drawn shrank the chart under
 * them, so the bars animated between two layouts and hung below the axis.
 *
 * @param {boolean} active  true only for the Total Scheduled Revenue pick
 * @param {string} win      window id, as resolveWindow takes it
 * @param {{from,to}|null} custom
 * @param {number} aheadDays  extra days past the period's end (look-ahead)
 * @returns {{series: {date:string, value:number}[]|null, status: "idle"|"loading"|"ready"|"error"}}
 */
export default function useScheduledSplit(active, win, custom, aheadDays = 0) {
  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";
  const [state, setState] = useState({ key: "", series: null, failed: false });

  const bounds = active ? resolveWindow(win, custom) : null;
  const from = bounds && bounds.from && bounds.from.isValid() ? bounds.from.format("YYYY-MM-DD") : "";
  /* To the period's END, not today: the bars show work booked for the days
     still to come (see useTotalRevenue's throughPeriodEnd). */
  const to = bounds && bounds.to && bounds.to.isValid()
    ? lookAheadEnd(periodEndFor(win, bounds.to, custom), aheadDays, companyToday()).format("YYYY-MM-DD")
    : "";
  const key = active && from && to ? `${from}|${to}` : "";

  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    GET_SCHEDULED_REVENUE_DAILY(from, to)
      .then((res) => {
        if (!alive) return;
        const days = res?.data?.days || res?.days || [];
        setState({ key, failed: false, series: days.map((d) => ({ date: d.date, value: Number(d.completed) || 0 })) });
      })
      .catch(() => {
        if (alive) setState({ key, series: null, failed: true });
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, win, customKey]);

  // A stale answer for a previous window must not shade the new one.
  const current = key && state.key === key;
  let status = "idle";
  if (key) {
    if (!current) status = "loading";
    else status = state.failed ? "error" : "ready";
  }
  return { series: current ? state.series : null, status };
}
